import React from "react";
import { Link } from "react-router-dom";
import { Shield, Star, Compass, Book, Gamepad2, Palette } from "lucide-react";
import { useApp } from "../context";
import { isAdminUser } from "../utils/adminCheck";
import LanguageSwitcher from "./LanguageSwitcher";

const SocialIcon = ({ href, label, children }) => {
  // When no real URL is configured yet we render a non-interactive "coming soon"
  // visual instead of a dead `href="#"` link so clicks don't scroll users to the
  // top of the page.
  const isLive = href && href !== "#";
  const common = {
    "aria-label": isLive ? label : `${label}, coming soon`,
    className: `w-8 h-8 rounded-lg flex items-center justify-center bg-white/5 border border-white/8 transition-all ${isLive ? "text-white/30 hover:text-white/70 hover:bg-white/10 hover:border-white/15" : "text-white/15 cursor-not-allowed"}`,
    "data-testid": `social-${label.toLowerCase()}`,
    title: isLive ? label : `${label} coming soon`,
  };
  if (!isLive) return <span {...common}>{children}</span>;
  return (
    <a href={href} target="_blank" rel="noreferrer" {...common}>
      {children}
    </a>
  );
};

const Footer = () => {
  const { user } = useApp();
  return (
  <footer className="relative overflow-hidden border-t border-white/10" data-testid="footer" style={{ background: "linear-gradient(180deg, rgba(15,20,40,0.98) 0%, rgba(8,10,24,0.99) 50%, rgba(4,6,16,1) 100%)" }}>
    {/* Starfield */}
    <div className="absolute inset-0 pointer-events-none overflow-hidden">
      {Array.from({ length: 30 }, (_, i) => (
        <div key={i} className="absolute rounded-full bg-white" style={{
          width: `${1 + Math.random() * 2}px`, height: `${1 + Math.random() * 2}px`,
          left: `${Math.random() * 100}%`, top: `${Math.random() * 100}%`,
          opacity: 0.1 + Math.random() * 0.2,
          animation: `star-pulse ${3 + Math.random() * 4}s ease-in-out ${Math.random() * 5}s infinite`,
        }} />
      ))}
      <div className="absolute top-0 left-0 w-full h-px" style={{ background: "linear-gradient(90deg, transparent, rgba(139,92,246,0.3), rgba(236,72,153,0.2), rgba(251,146,60,0.15), transparent)" }} />
    </div>

    <div className="relative max-w-5xl mx-auto px-4 py-8">
      {/* Navigation Dock */}
      <div className="flex flex-wrap justify-center gap-5 mb-6">
        {[
          { to: "/learn", icon: Book, label: "Learn", color: "#22C55E" },
          { to: "/arcade", icon: Gamepad2, label: "Arcade", color: "#8B5CF6" },
          { to: "/adventures", icon: Compass, label: "Adventures", color: "#F97316", comingSoon: true },
          { to: "/art-studio", icon: Palette, label: "Create", color: "#EC4899" },
          { to: "/trophies", icon: Star, label: "Trophies", color: "#F59E0B" },
        ].map(item => {
          const blocked = item.comingSoon && !isAdminUser(user);
          return blocked ? (
            <div key={item.to} className="flex flex-col items-center gap-1.5 opacity-30 cursor-not-allowed" data-testid={`footer-${item.label.toLowerCase()}`}>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${item.color}10`, border: `1.5px solid ${item.color}15` }}>
                <item.icon size={18} style={{ color: item.color }} className="opacity-50" />
              </div>
              <span className="text-white/30 text-[10px] font-fredoka font-bold">Soon</span>
            </div>
          ) : (
            <Link key={item.to} to={item.to} className="flex flex-col items-center gap-1.5 group" data-testid={`footer-${item.label.toLowerCase()}`}>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center transition-all group-hover:scale-110 group-hover:shadow-lg" style={{ background: `${item.color}18`, border: `1.5px solid ${item.color}30`, boxShadow: `0 2px 10px ${item.color}10` }}>
                <item.icon size={18} style={{ color: item.color }} />
              </div>
              <span className="text-white/60 text-[10px] font-fredoka font-bold group-hover:text-white/90 transition-colors">{item.label}</span>
            </Link>
          );
        })}
      </div>

      {/* Social Media Icons */}
      <div className="flex justify-center gap-2 mb-5" data-testid="social-icons">
        <SocialIcon href="#" label="Instagram">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/></svg>
        </SocialIcon>
        <SocialIcon href="#" label="TikTok">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1 0-5.78 2.92 2.92 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 3 15.57 6.33 6.33 0 0 0 9.37 22a6.33 6.33 0 0 0 6.37-6.23V9.34a8.16 8.16 0 0 0 3.85.97V6.69z"/></svg>
        </SocialIcon>
        <SocialIcon href="#" label="YouTube">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22.54 6.42a2.78 2.78 0 0 0-1.94-2C18.88 4 12 4 12 4s-6.88 0-8.6.46a2.78 2.78 0 0 0-1.94 2A29 29 0 0 0 1 11.75a29 29 0 0 0 .46 5.33A2.78 2.78 0 0 0 3.4 19.08c1.72.46 8.6.46 8.6.46s6.88 0 8.6-.46a2.78 2.78 0 0 0 1.94-2 29 29 0 0 0 .46-5.25 29 29 0 0 0-.46-5.41z"/><polygon points="9.75 15.02 15.5 11.75 9.75 8.48 9.75 15.02"/></svg>
        </SocialIcon>
        <SocialIcon href="#" label="Twitter">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
        </SocialIcon>
        <SocialIcon href="#" label="Facebook">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/></svg>
        </SocialIcon>
      </div>

      {/* Footer Links */}
      <div className="flex flex-col md:flex-row items-center justify-between gap-3 pt-4 border-t border-white/8">
        <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1">
          {[
            { to: "/help", label: "Help Center" },
            { to: "/contact", label: "Contact" },
            { to: "/about", label: "About" },
            { to: "/partners", label: "Partners" },
            { to: "/sponsor", label: "Sponsors" },
            { to: "/safety", label: "Safety" },
            { to: "/trust-safety", label: "Trust & Safety" },
            { to: "/report-safety", label: "Report Concern" },
            { to: "/testimonials", label: "Testimonials" },
            { to: "/pricing", label: "Pricing" },
            { to: "/learning-features", label: "Learning Features" },
            { to: "/how-it-works", label: "How It Works" },
            { to: "/privacy", label: "Privacy" },
            { to: "/terms", label: "Terms" },
            { to: "/ambassador-payouts", label: "Ambassador Payouts" },
            { to: "/coppa-notice", label: "COPPA" },
            { to: "/do-not-sell", label: "Do Not Sell My Info" },
            // Admin link is only shown to authenticated admins.
            ...(isAdminUser(user) ? [{ to: "/admin-dashboard", label: "Admin" }] : []),
          ].map(link => (
            <Link key={link.label} to={link.to} className="text-white/40 hover:text-white/70 text-[10px] font-semibold transition-colors" data-testid={`footer-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}>{link.label}</Link>
          ))}
          <button
            type="button"
            onClick={() => { try { window.dispatchEvent(new CustomEvent("isaiah-open-cookie-banner")); } catch { /* no-op */ } }}
            className="text-white/40 hover:text-white/70 text-[10px] font-semibold transition-colors"
            data-testid="footer-link-manage-cookies"
          >
            Manage Cookies
          </button>
        </div>
        <div className="flex items-center gap-1.5">
          <Shield size={12} className="text-emerald-400/60" />
          <span className="text-white/45 text-[10px] font-semibold">Safe & Secure</span>
          <span className="text-white/15 mx-1">|</span>
          <span className="text-white/35 text-[10px]">&copy; 2026 Isaiah World Entertainment LLC</span>
        </div>
      </div>

      {/* Company Info , clear labels for accessibility & scanning */}
      <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 mt-4 text-white/55 text-[11px]" data-testid="footer-company-info">
        <span className="flex items-center gap-1.5">
          <span className="font-bold uppercase tracking-wide text-white/35 text-[9px]">Email:</span>
          <a href="mailto:info@isaiahsworld.com" className="font-bold text-white/70 hover:text-white transition-colors" data-testid="footer-email">info@isaiahsworld.com</a>
        </span>
        <span className="flex items-center gap-1.5">
          <span className="font-bold uppercase tracking-wide text-white/35 text-[9px]">Phone Number:</span>
          <a href="tel:+19126003940" className="font-bold text-white/70 hover:text-white transition-colors" data-testid="footer-phone">(912) 600-3940</a>
        </span>
        <span className="flex items-center gap-1.5">
          <span className="font-bold uppercase tracking-wide text-white/35 text-[9px]">Mailing Address:</span>
          <span className="font-bold text-white/70" data-testid="footer-address">10520 Abercorn St, Ste D, Savannah, GA 31419</span>
        </span>
      </div>

      {/* Tagline + language switcher */}
      <div className="flex flex-col sm:flex-row items-center justify-center gap-2 mt-3">
        <p className="text-center text-white/40 text-[10px] tracking-wider font-fredoka font-bold">Where Kids, Parents &amp; Solo Explorers of All Ages Learn, Play, Create &amp; Explore</p>
        <span className="hidden sm:inline text-white/15">·</span>
        <LanguageSwitcher variant="footer" />
      </div>
    </div>
  </footer>
  );
};

export default Footer;
